import BlueskyIcon from '~/components/Icons/BlueskyIcon';
import DiscordIcon from '~/components/Icons/DiscordIcon';
import FacebookIcon from '~/components/Icons/FacebookIcon';
import InstagramIcon from '~/components/Icons/InstagramIcon';
import LinkedinIcon from '~/components/Icons/LinkedInIcon';
import RssFeedIcon from '~/components/Icons/RssFeedIcon';
import ThreadsIcon from '~/components/Icons/ThreadsIcon';
import TiktokIcon from '~/components/Icons/TiktokIcon';
import TwitterIcon from '~/components/Icons/TwitterIcon';
import YoutubeIcon from '~/components/Icons/YoutubeIcon';
import type { Publication } from '~/interfaces/publication';
import makeExternalUrl from '~/utils/makeExternalUrl';

interface IconProps {
  url?: string;
  icon: React.ReactNode;
  ariaLabel?: string;
  bgColor?: string;
  fillColor?: string;
}

const IconHelper = ({ url, icon, ariaLabel, bgColor, fillColor }: IconProps) => {
  if (!url) return null;

  return (
    <a target="_blank" rel="noreferrer" href={url} aria-label={ariaLabel} className="relative p-2">
      <div className="absolute left-0 top-0 h-full w-full rounded-full bg-black opacity-10" />
      <div
        className="rounded-full bg-black p-2 text-white"
        style={{
          backgroundColor: bgColor,
          color: fillColor,
        }}
      >
        {icon}
      </div>
    </a>
  );
};

interface Props {
  publication: Publication;
  bgColor?: string;
  fillColor?: string;
}

const SocialIcons = ({ publication, bgColor, fillColor }: Props) => {
  const {
    twitter_url,
    linkedin_url,
    youtube_url,
    tiktok_url,
    facebook_url,
    instagram_url,
    threads_url,
    discord_url,
    bluesky_url,
  } = publication;
  const rss_feed_url = publication?.rss_feeds?.[0]?.url;

  const icons = [
    {
      url: makeExternalUrl(twitter_url),
      icon: <TwitterIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Twitter',
    },
    {
      url: makeExternalUrl(linkedin_url),
      icon: <LinkedinIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'LinkedIn',
    },
    {
      url: makeExternalUrl(youtube_url),
      icon: <YoutubeIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Youtube',
    },
    {
      url: makeExternalUrl(tiktok_url),
      icon: <TiktokIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Tiktok',
    },
    {
      url: makeExternalUrl(facebook_url),
      icon: <FacebookIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Facebook',
    },
    {
      url: makeExternalUrl(instagram_url),
      icon: <InstagramIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Instagram',
    },
    {
      url: makeExternalUrl(threads_url),
      icon: <ThreadsIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Threads',
    },
    {
      url: makeExternalUrl(discord_url),
      icon: <DiscordIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Discord',
    },
    {
      url: makeExternalUrl(rss_feed_url),
      icon: <RssFeedIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'RSS',
    },
    {
      url: makeExternalUrl(bluesky_url),
      icon: <BlueskyIcon className="h-3 w-3" fill={'currentColor'} />,
      ariaLabel: 'Bluesky',
    },
  ];

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 sm:items-start sm:justify-start">
      {icons.map((icon, _index) => (
        <IconHelper
          key={icon.ariaLabel}
          url={icon.url}
          icon={icon.icon}
          ariaLabel={icon.ariaLabel}
          bgColor={bgColor}
          fillColor={fillColor}
        />
      ))}
    </div>
  );
};

export default SocialIcons;
